import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAgent,
  deleteAgent,
  getAgentDetail,
  getAgentRoster,
  getAgentUsers,
  getMyAgent,
  setMyChatStatus,
  updateAgent,
} from 'services/agents';
import type { UpdateAgentInput } from 'types/agents';

export const AGENT_ROSTER_QUERY_KEY = ['agents', 'roster'] as const;
export const AGENT_USERS_QUERY_KEY = ['agents', 'users'] as const;
export const MY_AGENT_QUERY_KEY = ['agents', 'me'] as const;

export function agentDetailQueryKey(userId: string) {
  return ['agents', 'detail', userId] as const;
}

export function useAgentRosterQuery() {
  return useQuery({
    queryKey: AGENT_ROSTER_QUERY_KEY,
    queryFn: getAgentRoster,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useAgentUsersQuery(enabled = true) {
  return useQuery({
    queryKey: AGENT_USERS_QUERY_KEY,
    queryFn: getAgentUsers,
    enabled,
    staleTime: 10_000,
  });
}

export function useMyAgentQuery() {
  return useQuery({
    queryKey: MY_AGENT_QUERY_KEY,
    queryFn: getMyAgent,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useAgentDetailQuery(userId: string | null) {
  return useQuery({
    queryKey: userId ? agentDetailQueryKey(userId) : ['agents', 'detail', 'empty'],
    queryFn: () => {
      if (!userId) return Promise.resolve(null);
      return getAgentDetail(userId);
    },
    enabled: Boolean(userId),
    refetchInterval: userId ? 30_000 : false,
    staleTime: 15_000,
  });
}

function useInvalidateAgents() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: AGENT_ROSTER_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: AGENT_USERS_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: MY_AGENT_QUERY_KEY }),
    ]);
}

export function useCreateAgentMutation() {
  const invalidate = useInvalidateAgents();
  return useMutation({ mutationFn: createAgent, onSuccess: () => invalidate() });
}

export function useUpdateAgentMutation() {
  const invalidate = useInvalidateAgents();
  return useMutation({
    mutationFn: (vars: { userId: string; patch: UpdateAgentInput }) =>
      updateAgent(vars.userId, vars.patch),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteAgentMutation() {
  const invalidate = useInvalidateAgents();
  return useMutation({ mutationFn: deleteAgent, onSuccess: () => invalidate() });
}

export function useSetMyChatStatusMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setMyChatStatus,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: MY_AGENT_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: AGENT_ROSTER_QUERY_KEY }),
      ]);
    },
  });
}
